/**
 * AudienceContext — global Personal vs Business audience selector for the
 * public-facing site. Persists to localStorage so a visitor stays in their
 * chosen track across page loads. Each new tab starts from the default
 * (personal) unless the user explicitly picked business previously.
 *
 * Wiring:
 *   - Wrapped around the app in App.js (between LanguageProvider and AuthProvider).
 *   - Consumed by Header.jsx (the audience switcher in the top-bar) and by
 *     any landing/marketing component that wants to render audience-specific
 *     CTAs.
 *   - The dashboard / admin routes ignore this — it's purely a marketing-IA
 *     concern.
 */
import { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const STORAGE_KEY = 'jtr_audience';
// Iter 65 (revised) — only Personal and Business are user-selectable
// audiences. The homepage and other generic pages don't set audience;
// it's the URL prefix that drives mode switching.
const VALID = ['personal', 'business', 'government'];

// Government-track marketing pages whose URLs do NOT start with /government
// (SEO-driven slugs). Without this, navigating to them from the Government
// "Resources & References" page silently drops the visitor out of Government
// mode — the gov subnav / mode strip vanishes and the page looks "menu-less".
// Keep this in sync with the gov-track <Route>s in App.js.
const GOV_SLUG_PATTERN = new RegExp(
  '^(?:/(?:ja|en))?/(?:' +
    [
      'government',                                  // /government, /government-*
      'usfj-sofa-toll-certificates-japan',
      'japanese-police-fire-emergency-vehicle-toll-rules',
      'japanese-official-convoy-toll-payments',
      'diet-members-official-travel-tolls-japan',
      'who-pays-government-vehicle-tolls-japan',
      'mofa-diplomatic-official-vehicle-tolls-japan',
    ].join('|') +
    ')'
);

const AudienceContext = createContext({
  audience: null,
  setAudience: () => {},
});

export function AudienceProvider({ children }) {
  const [audience, setAudienceState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return VALID.includes(saved) ? saved : null;
    } catch {
      return null;
    }
  });

  const setAudience = (next) => {
    if (next !== null && !VALID.includes(next)) return;
    setAudienceState(next);
    try {
      if (next === null) localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // localStorage may be disabled (private mode) — graceful fallback.
    }
  };

  // Auto-sync audience to URL on EVERY pathname change so the mode
  // strip / nav switches correctly whenever the visitor navigates —
  // including clicks on the cross-track Live Counter banner pills.
  // On generic / homepage URLs, audience is reset to null (Overview).
  const location = useLocation();
  useEffect(() => {
    const path = location?.pathname || '';
    if (path.startsWith('/business') || /^\/(ja|en)\/business/.test(path)) {
      setAudience('business');
    } else if (GOV_SLUG_PATTERN.test(path)) {
      setAudience('government');
    } else if (path.startsWith('/personal') || /^\/(ja|en)\/personal/.test(path)) {
      setAudience('personal');
    } else {
      setAudience(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.pathname]);

  return (
    <AudienceContext.Provider value={{ audience, setAudience }}>
      {children}
    </AudienceContext.Provider>
  );
}

export function useAudience() {
  return useContext(AudienceContext);
}
